import { useState } from 'react';
import type { AdminProduct } from '@popcart/core/v1';
import { errorMessage } from '../api';
import { Icon } from '../ui/Icon';
import { useToast } from '../ui/toast';
import { deltaLabel, stockStep, validateStockChange } from './lib/list.js';
import { useAdjustStock } from './hooks';
import { Select } from '../ui/controls';

type Reason = 'restock' | 'adjust';

/** Quick +/− stock with a reason (restock / correction). Used in list rows and the editor. */
export function StockAdjust({ product, compact, onDone }: { product: Pick<AdminProduct, 'id' | 'shopId' | 'name' | 'onHand' | 'stockUnit'>; compact?: boolean; onDone?(): void }) {
  const toast = useToast();
  const m = useAdjustStock();
  const [delta, setDelta] = useState(0);
  const [reason, setReason] = useState<Reason>('restock');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const unit = product.stockUnit;
  const step = stockStep(unit);
  const id = `stk-${product.id}${compact ? '-row' : ''}`;

  const bump = (n: number) => {
    setError(null);
    const next = delta + n * step;
    setDelta(next);
    if (next < 0 && reason === 'restock') setReason('adjust');
  };

  const save = () => {
    const v = validateStockChange({ delta, reason, note, onHand: product.onHand, unit });
    if (!v.ok) {
      setError(v.error);
      return;
    }
    setError(null);
    m.mutate(
      { shopId: product.shopId, productId: product.id, delta: v.delta, reason: v.reason, note: v.note },
      {
        onSuccess: () => {
          toast(`${product.name}: ${deltaLabel(v.delta, unit)} saved.`);
          setDelta(0);
          setNote('');
          setReason('restock');
          onDone?.();
        },
        onError: (err) => setError(errorMessage(err, "Couldn't save. Try again.")),
      },
    );
  };

  return (
    <div className="stack s4">
      <div className="row wrap">
        <div className="stepper" role="group" aria-label={`Stock change for ${product.name}`}>
          <button type="button" className="btn icon-btn" aria-label={`Remove ${deltaLabel(step, unit).slice(1)} of ${product.name}`} onClick={() => bump(-1)} disabled={product.onHand + delta - step < 0}>
            <Icon name="minus" />
          </button>
          <output aria-live="polite" aria-label={`Change for ${product.name}`}>
            {deltaLabel(delta, unit)}
          </output>
          <button type="button" className="btn icon-btn" aria-label={`Add ${deltaLabel(step, unit).slice(1)} of ${product.name}`} onClick={() => bump(1)}>
            <Icon name="plus" />
          </button>
        </div>
        <label className="sr-only" htmlFor={`${id}-reason`}>
          Reason
        </label>
        <Select id={`${id}-reason`} className="input cat-auto" value={reason} onChange={(e) => setReason(e.target.value as Reason)}>
          <option value="restock">Restock</option>
          <option value="adjust">Correction</option>
        </Select>
        {delta !== 0 ? (
          <>
            <label className="sr-only" htmlFor={`${id}-note`}>
              Note
            </label>
            <input
              id={`${id}-note`}
              className="input cat-note"
              placeholder={delta < 0 ? 'Why? (required)' : 'Note (optional)'}
              maxLength={200}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? `${id}-err` : undefined}
            />
          </>
        ) : null}
        <button type="button" className="btn" disabled={delta === 0 || m.isPending} aria-busy={m.isPending || undefined} onClick={save}>
          {m.isPending ? 'Saving…' : 'Save stock'}
        </button>
      </div>
      {error ? (
        <span id={`${id}-err`} className="pill st-error err-pill cat-wrap" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
